import * as React from "react";
import * as v from "valibot";
import { useStateContext } from "../state";
import { isEqual, pick, omitBy } from "lodash";
import { FieldPath } from "react-hook-form";
import { useValibotForm, UseValibotFormReturn } from "@/utils/validation";
import { FormattedMessage } from "react-intl";
import {
  Form,
  FormField,
  FormControl,
  FormLabel,
  FormItem,
  FormMessage,
} from "@/kit/ui/form";
import { Switch } from "@/kit/ui/switch";

const DEFAULT_INFER_OPTIONS = {
  forbid_null_fields: false,
  sequence_as_small_list: false,
  string_as_small_utf8: false,
  bytes_as_small_binary: false,
  no_coerce_numbers: false,
  forbid_to_string: false,
  no_guess_dates: false,
};

const schemaOptionsSchema = v.object({
  forbid_null_fields: v.boolean(),
  sequence_as_small_list: v.boolean(),
  string_as_small_utf8: v.boolean(),
  bytes_as_small_binary: v.boolean(),
  no_coerce_numbers: v.boolean(),
  forbid_to_string: v.boolean(),
  no_guess_dates: v.boolean(),
});

type SchemaOptionsSchema = typeof schemaOptionsSchema;

interface SwitchField {
  form: UseValibotFormReturn<SchemaOptionsSchema>;
  name: FieldPath<v.InferInput<SchemaOptionsSchema>>;
  label: React.ReactNode;
}

const SwitchField = ({ form, name, label }: SwitchField) => {
  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => (
        <FormItem checkbox>
          <FormControl>
            <Switch className="block" {...field} />
          </FormControl>
          <div>
            <FormLabel>{label}</FormLabel>
            <FormMessage />
          </div>
        </FormItem>
      )}
    ></FormField>
  );
};

export const InferOptions = () => {
  const { inferSchemaOptions, setInferSchemaOptions } = useStateContext();
  const form = useValibotForm(schemaOptionsSchema, {
    defaultValues: {
      ...DEFAULT_INFER_OPTIONS,
      ...pick(inferSchemaOptions, [
        "forbid_null_fields",
        "sequence_as_small_list",
        "string_as_small_utf8",
        "bytes_as_small_binary",
        "no_coerce_numbers",
        "forbid_to_string",
        "no_guess_dates",
      ]),
    },
    reValidateMode: "onChange",
  });
  const value = form.watch();
  React.useEffect(() => {
    const newSchemaOptions = omitBy(
      {
        ...inferSchemaOptions,
        ...value,
      },
      (val) => !val,
    );
    if (!isEqual(newSchemaOptions, inferSchemaOptions)) {
      setInferSchemaOptions(newSchemaOptions);
    }
  }, [value, inferSchemaOptions, setInferSchemaOptions]);
  return (
    <Form form={form} className="flex flex-col gap-4">
      <h2 className="text-l font-semibold">
        <FormattedMessage defaultMessage="Inference Options" />
      </h2>
      <SwitchField
        form={form}
        name="forbid_null_fields"
        label={<FormattedMessage defaultMessage="Forbid null fields" />}
      />
      <SwitchField
        form={form}
        name="sequence_as_small_list"
        label={<FormattedMessage defaultMessage="Sequence as small list" />}
      />
      <SwitchField
        form={form}
        name="string_as_small_utf8"
        label={<FormattedMessage defaultMessage="Strings as small utf8" />}
      />
      <SwitchField
        form={form}
        name="bytes_as_small_binary"
        label={<FormattedMessage defaultMessage="Bytes as small binary" />}
      />
      <SwitchField
        form={form}
        name="no_coerce_numbers"
        label={<FormattedMessage defaultMessage="Do not coerce numbers" />}
      />
      <SwitchField
        form={form}
        name="forbid_to_string"
        label={
          <FormattedMessage defaultMessage="Do not allow conversion to string" />
        }
      />
      <SwitchField
        form={form}
        name="no_guess_dates"
        label={<FormattedMessage defaultMessage="Do not guess dates" />}
      />
    </Form>
  );
};
